/**
 * Collection filter cards
 *
 * Cards are plain links to server-side filter URLs (they work without JS).
 * Clicks are intercepted and routed through CollectionProducts.applyFilter
 * so the grid updates in place instead of reloading. Active state re-syncs
 * whenever filters change (card or popup driven).
 *
 * Cards are single-select, like the product-type chips: each card's URL
 * replaces the current filter query, and pressing the active card clears its
 * filters so all products come back. Clicks are delegated on the container so
 * the behaviour survives the card row being re-rendered or swapped.
 */

import { collapseSingleValueParams, urlWithoutCardFilters } from '../utils/filter-chips.js';
import { enableDragScroll } from '../utils/drag-scroll.js';

export default class FilterCards {
  constructor(container, products) {
    this.container = container;
    this.products = products;

    this.container.addEventListener('click', this.onCardClick);
    this.container.addEventListener('collection:filtered', this.syncCardActive);

    // The row is a native scroller (touch / trackpad); mouse users can also
    // click-and-drag it on desktop and laptop viewports.
    enableDragScroll(this.container.querySelector('.filter-cards__track'));
  }

  // Re-queried on every use so swapped-in cards are picked up.
  getCards = () => Array.from(this.container.querySelectorAll('[data-filter-card]'));

  onCardClick = (e) => {
    // Let modified clicks (new tab / middle-click) behave normally.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    const card = e.target.closest('[data-filter-card]');
    if (!card || !this.container.contains(card)) return;
    if (!this.products?.applyFilter) return;

    e.preventDefault();

    // Pressing the active card clears it — the same "press again to reset"
    // behaviour as the product-type chips.
    const target = card.classList.contains('is-active')
      ? urlWithoutCardFilters(card.href)
      : collapseSingleValueParams(card.href);

    this.products.applyFilter(target);
  };

  // A card is active when every `filter.*` param in its link is present in the
  // current URL — mirrors the server-side (Liquid) active check.
  syncCardActive = () => {
    const current = new URL(window.location.href).searchParams;

    this.getCards().forEach((card) => {
      const cardParams = new URL(card.href).searchParams;
      let hasFilter = false;
      let active = true;

      for (const [key, value] of cardParams.entries()) {
        if (!key.startsWith('filter.')) continue;
        hasFilter = true;
        if (!current.getAll(key).includes(value)) {
          active = false;
          break;
        }
      }

      card.classList.toggle('is-active', hasFilter && active);
    });
  };
}
